import { useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

const IRELAND = [53.35, -7.7];

function ClickToPin({ onPick }) {
  useMapEvents({ click: (e) => onPick(+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)) });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  if (center) map.setView(center, Math.max(map.getZoom(), 16));
  return null;
}

export function SiteLocationPicker({ lat, lng, radius, address, onChange }) {
  const [q, setQ] = useState(address || "");
  const [busy, setBusy] = useState(false);
  const [fly, setFly] = useState(null);
  const has = lat != null && lat !== "" && lng != null && lng !== "";
  const r = Number(radius) || 150;

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } });
      const data = await res.json();
      if (!data.length) { toast.error("Address not found. Try adding the town or Eircode, or drop a pin on the map."); return; }
      const p = [+(+data[0].lat).toFixed(6), +(+data[0].lon).toFixed(6)];
      onChange({ lat: p[0], lng: p[1] }); setFly(p);
    } catch { toast.error("Address search is unavailable. Drop a pin on the map instead."); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="site-location-picker">
      <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Geo-fence location</Label>
      <div className="flex gap-2">
        <Input data-testid="s-geo-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder="Search address or Eircode" />
        <Button type="button" data-testid="s-geo-search-btn" variant="outline" onClick={search} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
      </div>
      <div className="h-56 rounded-md overflow-hidden border border-zinc-200 relative z-0" data-testid="s-geo-map">
        <MapContainer center={has ? [lat, lng] : IRELAND} zoom={has ? 16 : 6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickToPin onPick={(a, b) => onChange({ lat: a, lng: b })} />
          {fly && <FlyTo center={fly} />}
          {has && <><Circle center={[lat, lng]} radius={r} pathOptions={{ color: "#f59e0b", fillOpacity: 0.15 }} /><CircleMarker center={[lat, lng]} radius={6} pathOptions={{ color: "#0f172a", fillColor: "#0f172a", fillOpacity: 1 }} /></>}
        </MapContainer>
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <div><Label className="text-[11px] text-zinc-500">Latitude</Label><Input data-testid="s-lat" value={lat ?? ""} onChange={(e) => onChange({ lat: e.target.value === "" ? null : e.target.value })} className="h-8" /></div>
        <div><Label className="text-[11px] text-zinc-500">Longitude</Label><Input data-testid="s-lng" value={lng ?? ""} onChange={(e) => onChange({ lng: e.target.value === "" ? null : e.target.value })} className="h-8" /></div>
        <div><Label className="text-[11px] text-zinc-500">Radius (m)</Label><Input data-testid="s-radius" type="number" min="25" value={radius ?? 150} onChange={(e) => onChange({ geofence_radius: e.target.value })} className="h-8" /></div>
      </div>
      <p className="text-[11px] text-zinc-400">Click the map to drop or move the pin. Staff must be inside the circle to clock in or out.</p>
    </div>
  );
}
